import React, { useEffect, useRef, useMemo, useState } from "react";
import LcFetch from "lib/shared/fetch";
import GuideListViewControl from "components/shared/GuideList/GuideListViewControl";
import GuideListSortControl from "components/shared/GuideList/GuideListSortControl";
import GuideListAlphabeticalFilterControl from "components/shared/GuideList/GuideListAlphabeticalFilterControl";
import helpers from "helpers/base";
import camelize from "helpers/camelize";
import { toastNotify } from "helpers/toastNotify";
import LitCard from "components/shared/LitCard";
import Icon from "components/shared/Icon";
import { subscribe, unsubscribe } from "helpers/pubsub";
import litCards from "views/shared/litCards";

const viewOptions = {
  grid: "grid",
  list: "list"
};

const sortOptions = {
  author: "author",
  recent: "recent",
  title: "title"
};

const sortOrders = {
  asc: "asc",
  desc: "desc"
};

const filterOptions = {
  all: "All",
  number: "#"
};

const queryParamKeys = {
  filterOption: "filter",
  sortOption: "sort",
  sortOrder: "order",
  viewOption: "view"
};

const defaultProps = {
  defaultFilterOption: filterOptions.all,
  defaultSortOption: sortOptions.title,
  defaultSortOrder: sortOrders.asc,
  defaultViewOption: viewOptions.list
};

const typesOfWork = {
  proseFiction: "Prose Fiction",
  proseNonfiction: "Prose Nonfiction"
};

const friendlyTypesOfWork = {
  fiction: "Fiction",
  nonfiction: "Nonfiction"
};

const selectors = {
  BACK_TO_TOP: ".guide-list__back-to-top",
  BACK_TO_TOP_HIDDEN: ".guide-list__back-to-top--hidden",
  CONTROLS: ".guide-list__controls",
  GLOBAL_FOOTER: "section.footer",
  GROUP: ".guide-list__group",
  GROUPS_GRID: ".guide-list__groups--grid",
  GROUPS_LIST: ".guide-list__groups--list",
  GUIDE: ".guide-list__guide",
  LIT_CARD: "#all .guide-list__guide .lit-card",
  MAIN_CONTROLS_FIXED: ".guide-list--with-controls-fixed"
};

const controlsOffset = 62; // number of px from the top of .guide-list to top of .guide-list__controls

const getFriendlyTypeOfWork = (typeOfWork) => {
  if (typeOfWork === typesOfWork.proseFiction) {
    return friendlyTypesOfWork.fiction;
  }
  if (typeOfWork === typesOfWork.proseNonfiction) {
    return friendlyTypesOfWork.nonfiction;
  }
  return typeOfWork;
};

const getOptionsFromQueryParams = () => {
  const options = {};
  helpers.array.each(["viewOption", "sortOption", "filterOption"], (option) => {
    const param = helpers.getQueryParam(queryParamKeys[option]);
    if (param) {
      options[option] = param;
    }
    if (option === "filterOption" && param === "number") {
      options[option] = "number";
    }
  });
  return options;
};

export default function GuideList({ fetchPath, title, userSignedIn }) {
  const queryParams = getOptionsFromQueryParams();

  const [viewOption, setViewOption] = useState(() => {
    return queryParams.viewOption || viewOptions.list;
  });
  const [sortOption, setSortOption] = useState(() => {
    return queryParams.sortOption || sortOptions.title;
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return queryParams.sortOrder || sortOrders.asc;
  });
  const [filterOption, setFilterOption] = useState(() => {
    return queryParams.filterOption || filterOptions.all;
  });
  const [page, setPage] = useState(0);
  const [guides, setGuides] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const displayCount = guides.length;
  const queryParamOptions = { filterOption, sortOption, viewOption };
  const mainRef = useRef(null);

  const getFallbackAuthorName = (guide) => {
    return guide.authorLastName ? guide.authorLastName : guide.authorName;
  };

  const sortAuthorFn = (a, b) => {
    const aSortValue = getFallbackAuthorName(a);
    const bSortValue = getFallbackAuthorName(b);
    if (aSortValue < bSortValue) {
      return -1;
    }
    if (aSortValue > bSortValue) {
      return 1;
    }
    return 0;
  };

  const groups = useMemo(() => {
    switch (sortOption) {
      case sortOptions.title: {
        const grouped = helpers.array.group(guides, (guide) => {
          if (helpers.string.isNumber(guide.sortableTitle[0])) {
            return "#";
          }
          return guide.sortableTitle[0].toUpperCase();
        });
        Object.keys(grouped).forEach((key) => {
          grouped[key] = helpers.array.sortBy(grouped[key], "sortableTitle");
        });
        return grouped;
      }
      case sortOptions.author: {
        const newGroups = helpers.array.group(
          guides,
          (guide) => getFallbackAuthorName(guide)[0]
        );
        Object.keys(newGroups).forEach((key) => {
          newGroups[key] = newGroups[key].sort(sortAuthorFn);
        });
        return newGroups;
      }
      default:
        return { "Recently added": guides };
    }
  }, [guides]);

  const fetchGuides = () => {
    const url = `${fetchPath}?${new URLSearchParams({
      filter: filterOption,
      page,
      sort: sortOption,
      sortOrder
    })}`;
    LcFetch(url, {
      headers: {
        Accept: "application/json"
      },
      method: "GET"
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        const camelizedGuides = camelize(data.guides);
        const newGuides =
          page > 0 ? [...guides, ...camelizedGuides] : camelizedGuides;
        setGuides(newGuides);
        setTotalCount(data.total_count);
      })
      .catch(() => {
        toastNotify("Loading guides failed. Please try again.");
      });
  };

  const toggleBackToTopVisibility = () => {
    const element = document.querySelector(selectors.BACK_TO_TOP);
    const main = mainRef.current;
    const scrollPosition = helpers.dom.getScrollPosition();

    if (scrollPosition.top > main.offsetTop + 5) {
      element.classList.remove(selectors.BACK_TO_TOP_HIDDEN.substring(1));
    } else {
      element.classList.add(selectors.BACK_TO_TOP_HIDDEN.substring(1));
    }
  };

  const toggleControlsFixedToTop = () => {
    const main = mainRef.current;
    if (helpers.dom.getScrollPosition().top > main.offsetTop + controlsOffset) {
      main.classList.add(selectors.MAIN_CONTROLS_FIXED.substring(1));
    } else {
      main.classList.remove(selectors.MAIN_CONTROLS_FIXED.substring(1));
    }
  };

  const updateQueryParams = () => {
    const params = {};
    helpers.array.each(
      ["viewOption", "sortOption", "filterOption"],
      (option) => {
        const key = queryParamKeys[option];
        const defaultValue =
          defaultProps[
            `default${helpers.string.capitalizeFirstLetter(option)}`
          ];
        if (defaultValue !== queryParamOptions[option]) {
          params[key] = queryParamOptions[option];
        }
        if (option === "filterOption" && queryParamOptions[option] === "#") {
          params[key] = "number";
        } // hashtags not allowed as query param
      }
    );

    helpers.updateQueryParams(params);
  };

  const onScroll = () => {
    toggleBackToTopVisibility();
    toggleControlsFixedToTop();
  };

  useEffect(() => {
    subscribe("scroll", onScroll);

    return () => {
      unsubscribe("scroll", onScroll);
    };
  }, []);

  // Hook to fetch data
  useEffect(() => {
    fetchGuides(false);
  }, [sortOrder, sortOption, filterOption, page]);

  useEffect(() => {
    updateQueryParams();

    if (viewOption === viewOptions.grid) {
      litCards.randomizeBackgrounds(selectors.LIT_CARD);
    }
  }, [guides, viewOption]);

  const onSetViewOption = (viewOptionSelected) => {
    setViewOption(viewOptionSelected);
  };

  const onSetSortOption = (sortOptionSelected, click = true) => {
    const order =
      sortOptionSelected === sortOptions.recent
        ? sortOrders.desc
        : sortOrders.asc;
    setSortOrder(order);
    setSortOption(sortOptionSelected);
    setPage(0);

    if (click) {
      scrollToTopOfGuideList();
    }
  };

  const onSetFilterOption = (filterOptionSelected, click = true) => {
    filterOptionSelected === filterOptions.number
      ? setFilterOption("number")
      : setFilterOption(filterOptionSelected);
    setPage(0);

    if (click) {
      scrollToTopOfGuideList();
    }
  };

  const onClickLoadMore = () => {
    setPage(page + 1);
  };

  const scrollToTopOfGuideList = () => {
    helpers.dom.scrollTo(mainRef.current);
  };

  return (
    <div className="guide-list" data-testid="GuideList" ref={mainRef}>
      <div className="container--max-1170-with-padding">
        <div className="guide-list__title">
          <div className="title-3">{title}</div>
        </div>
      </div>
      <div className="guide-list__controls-wrapper">
        <div className="container--max-1170-with-padding guide-list__controls body-2">
          <GuideListViewControl
            queryParamOptions={queryParamOptions}
            viewOptions={viewOptions}
            defaultViewOption={viewOption}
            onSetViewOption={onSetViewOption}
          />
          <GuideListSortControl
            queryParamOptions={queryParamOptions}
            sortOptions={sortOptions}
            defaultSortOption={sortOption}
            onSetSortOption={onSetSortOption}
          />
          <GuideListAlphabeticalFilterControl
            queryParamOptions={queryParamOptions}
            baseFilterOptions={filterOptions}
            defaultFilterOption={filterOption}
            onSetFilterOption={onSetFilterOption}
          />
        </div>
      </div>
      <div className="guide-list__groups-wrapper">
        {viewOption === viewOptions.list && <GuidesAsList groups={groups} />}
        {viewOption === viewOptions.grid && (
          <GuidesAsGrid groups={groups} userSignedIn={userSignedIn} />
        )}
      </div>
      {totalCount > 0 ? (
        <div className="text-center guide-list__footer">
          <div className="body-2">{`You've viewed ${displayCount} of ${totalCount} guides`}</div>
          {displayCount < totalCount && (
            <button
              className="btn btn-lg btn-default"
              onClick={onClickLoadMore}
              type="button"
            >
              Load More
            </button>
          )}
        </div>
      ) : (
        <div className="text-center guide-list__empty container--max-1170-with-padding body-2">
          None of our titles start with this letter.
        </div>
      )}
      <div className="container--max-1170-with-padding guide-list__back-to-top guide-list__back-to-top--hidden">
        <button
          className="btn btn-default btn-icon"
          onClick={scrollToTopOfGuideList}
          type="button"
        >
          <Icon name="arrow-up" size="sm" />
        </button>
      </div>
    </div>
  );
}

function GuidesAsList({ groups }) {
  const keys = Object.keys(groups);

  return (
    <ul className="guide-list__groups guide-list__groups--list">
      {keys.map((key) => (
        <li className="guide-list__group" key={key}>
          <div className="guide-list__group--header">
            <div className="title-1">{key}</div>
          </div>
          <ul className="container-fluid guide-list__guides">
            {groups[key].map((guide) => (
              <a
                href={guide.url}
                className="guide-list__guide"
                key={guide.slug}
              >
                <div className="guide-list__guide-content">
                  <div className="guide-list__guide--title-author">
                    <div className="title-5">{guide.title}</div>
                    <div className="body-2">{guide.authorName}</div>
                  </div>
                  {guide.typeOfWork && (
                    <div className="guide-list__guide--type subtitle-2">
                      {getFriendlyTypeOfWork(guide.typeOfWork)}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function GuidesAsGrid({ groups, userSignedIn }) {
  const keys = Object.keys(groups);
  let index = 0;

  return (
    <ul className="guide-list__groups guide-list__groups--grid">
      {keys.map((key) => (
        <li className="guide-list__group" key={key}>
          <div className="guide-list__group--header">
            <div className="title-1">{key}</div>
          </div>
          <ul className="guide-list__guides">
            <div className="row">
              {groups[key].map((guide) => (
                <div
                  className="col-xxs-12 col-xs-6 col-sm-6 col-md-4 col-lg-4"
                  key={guide.slug}
                >
                  <li className="guide-list__guide">
                    <LitCard
                      authorName={guide.authorName}
                      bookmarkId={guide.bookmarkId}
                      index={index++} // eslint-disable-line no-plusplus
                      {...guide}
                      randomizeBackgrounds={false}
                      userSignedIn={userSignedIn}
                    />
                  </li>
                </div>
              ))}
            </div>
          </ul>
        </li>
      ))}
    </ul>
  );
}
